import type { ReactNode } from 'react';
import type { Opportunity } from '../../../types';

interface RecentDealsProps {
  deals: Opportunity[];
  loading?: boolean;
}

function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function RecentDeals({ deals, loading }: RecentDealsProps): ReactNode {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Recent Deals Won</h3>
        <a href="/pipeline" className="text-sm text-ds-primary hover:text-ds-primary/80">
          View all →
        </a>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-500">
          Loading deals...
        </div>
      ) : deals.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <p>No deals won yet</p>
          <p className="text-sm mt-1">Close your first deal to see it here!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deals.map((deal) => (
            <div
              key={deal.id}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-lg">🎉</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">{deal.name}</p>
                  <p className="text-sm text-gray-500">
                    {deal.actual_close_date && formatDate(deal.actual_close_date)}
                  </p>
                </div>
              </div>
              <div className="text-right ml-4">
                <p className="font-semibold text-ds-success">
                  {formatCurrency(deal.amount, deal.currency)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
